import React, { useState, useEffect, useCallback } from 'react';
import { User, Activity, RefreshCw, Download, Search, AlertTriangle, CheckCircle, DollarSign, RotateCcw, Ban, Send } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatAmount = (amount, currency = 'XOF') => {
  if (!amount && amount !== 0) return null;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 0 })?.format(amount);
};

const ACTION_CONFIG = {
  release: { label: 'Libération fonds', icon: CheckCircle, color: 'bg-green-100 text-green-700 border-green-200', dot: 'bg-green-500' },
  dispute: { label: 'Litige ouvert', icon: AlertTriangle, color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500' },
  refund: { label: 'Remboursement', icon: RotateCcw, color: 'bg-purple-100 text-purple-700 border-purple-200', dot: 'bg-purple-500' },
  resolve: { label: 'Litige résolu', icon: CheckCircle, color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
  cancel: { label: 'Annulation', icon: Ban, color: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' },
  commission_payout: { label: 'Commission créée', icon: DollarSign, color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
  commission_release: { label: 'Commission libérée', icon: Send, color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
};

const EscrowActionLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 20;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        ?.from('escrow_action_logs')
        ?.select(`
          *,
          escrow_transaction:escrow_transactions(id, reference, title, item_title, amount_total, total_amount, currency)
        `)
        ?.order('created_at', { ascending: false })
        ?.range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
      setLogs(prev => page === 0 ? (data || []) : [...prev, ...(data || [])]);
    } catch (err) {
      console.error('Error fetching action logs:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Subscribe to realtime
  useEffect(() => {
    const channel = supabase
      ?.channel('escrow_action_logs_realtime')
      ?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'escrow_action_logs' }, () => {
        if (page === 0) fetchLogs();
      })
      ?.subscribe();
    return () => { supabase?.removeChannel(channel); };
  }, [fetchLogs, page]);

  const exportCSV = () => {
    const rows = [['Date', 'Admin', 'Action', 'Transaction', 'Statut précédent', 'Nouveau statut', 'Montant', 'Notes']];
    logs?.forEach(log => {
      rows?.push([
        formatDateTime(log?.created_at),
        log?.admin_email || log?.admin_user_id?.slice(0, 8) || '—',
        ACTION_CONFIG?.[log?.action]?.label || log?.action,
        log?.escrow_transaction?.reference || log?.escrow_transaction_id?.slice(0, 8) || '—',
        log?.previous_status || '—',
        log?.new_status || '—',
        log?.amount_affected ? `${log?.amount_affected} ${log?.currency || 'XOF'}` : '—',
        log?.notes || '—',
      ]);
    });
    const csv = '\uFEFF' + rows?.map(r => r?.map(v => `"${String(v)?.replace(/"/g, '""')}"`)?.join(';'))?.join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `escrow-action-log-${new Date()?.toISOString()?.slice(0, 10)}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const filtered = logs?.filter(log => {
    const matchAction = actionFilter === 'ALL' || log?.action === actionFilter;
    const q = search?.toLowerCase();
    const matchSearch = !q ||
      log?.admin_email?.toLowerCase()?.includes(q) ||
      log?.notes?.toLowerCase()?.includes(q) ||
      log?.escrow_transaction?.reference?.toLowerCase()?.includes(q) ||
      log?.action?.toLowerCase()?.includes(q);
    return matchAction && matchSearch;
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-500" />
            Journal des actions admin
          </h4>
          <p className="text-xs text-gray-500 mt-0.5">Horodatage, utilisateur et action effectuée sur chaque transaction séquestre</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setPage(0); fetchLogs(); }} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={exportCSV} className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
            <Download className="w-4 h-4" />
            Exporter CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Rechercher par admin, référence, notes..." className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
        </div>
        <select value={actionFilter} onChange={e => setActionFilter(e?.target?.value)} className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
          <option value="ALL">Toutes les actions</option>
          {Object.entries(ACTION_CONFIG)?.map(([key, cfg]) => (
            <option key={key} value={key}>{cfg?.label}</option>
          ))}
        </select>
      </div>

      {/* Log timeline */}
      {loading && page === 0 ? (
        <div className="p-12 text-center"><RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" /><p className="text-gray-500 text-sm">Chargement du journal...</p></div>
      ) : filtered?.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-xl border border-gray-200">
          <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Aucune action enregistrée</p>
          <p className="text-gray-400 text-sm mt-1">Les actions admin sur les transactions séquestre apparaîtront ici</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Horodatage</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Admin</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Action</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Transaction</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Statut</th>
                  <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Montant</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered?.map(log => {
                  const actionCfg = ACTION_CONFIG?.[log?.action] || { label: log?.action, color: 'bg-gray-100 text-gray-600 border-gray-200', dot: 'bg-gray-400' };
                  const ActionIcon = actionCfg?.icon || Activity;
                  const txRef = log?.escrow_transaction?.reference || log?.escrow_transaction_id?.slice(0, 8) || '—';
                  const txTitle = log?.escrow_transaction?.title || log?.escrow_transaction?.item_title;
                  return (
                    <tr key={log?.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${actionCfg?.dot}`} />
                          <div>
                            <p className="text-xs font-medium text-gray-900">{formatDateTime(log?.created_at)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <User className="w-3.5 h-3.5 text-gray-500" />
                          </div>
                          <p className="text-xs text-gray-700 truncate max-w-[120px]">{log?.admin_email || log?.admin_user_id?.slice(0, 12) || 'Système'}</p>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${actionCfg?.color}`}>
                          <ActionIcon className="w-3 h-3" />
                          {actionCfg?.label}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-xs font-mono text-gray-600 bg-gray-100 px-2 py-0.5 rounded inline-block">{txRef}</p>
                        {txTitle && <p className="text-xs text-gray-400 mt-0.5 truncate max-w-[140px]">{txTitle}</p>}
                      </td>
                      <td className="px-5 py-4">
                        {(log?.previous_status || log?.new_status) && (
                          <div className="flex items-center gap-1.5 text-xs">
                            {log?.previous_status && <span className="text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{log?.previous_status}</span>}
                            {log?.previous_status && log?.new_status && <span className="text-gray-300">→</span>}
                            {log?.new_status && <span className="text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded font-medium">{log?.new_status}</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right">
                        {log?.amount_affected ? (
                          <span className="text-xs font-semibold text-amber-700">{formatAmount(log?.amount_affected, log?.currency)}</span>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-5 py-4 max-w-[200px]">
                        {log?.notes ? (
                          <p className="text-xs text-gray-500 truncate" title={log?.notes}>{log?.notes}</p>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Load more */}
          {filtered?.length >= PAGE_SIZE && (
            <div className="px-5 py-3 border-t border-gray-100 text-center">
              <button onClick={() => setPage(p => p + 1)} disabled={loading} className="text-sm text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50">
                {loading ? 'Chargement...' : 'Charger plus'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EscrowActionLog;
